import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LEARN } from "../../theme";
import { useBreakpoint } from "../../shell/LearnShell";
import { useDropTarget } from "../../interaction/useDragEngine";
import { ConfettiBurst } from "../../feedback/Celebration";
import {
  CrossIcon,
  DropHint,
  captionPill,
  fitTiles,
  PopIn,
  RoundCounter,
  ShakeWrap,
  Tile,
  frosted,
  gameShell,
  headerChip,
  itemKey,
  pickRoundItems,
  pieceButtonProps,
  seededShuffle,
  useCompleteOnce,
  useDragAndTap,
  useLetterAccent,
  useWrongFlash,
  wellStyle,
} from "./engineUtils";
import { SparkleBurst, useArenaMetrics } from "./tapUtils";

/** E "Starts with E?" ships its banks as shortE/otherE/otherStarting lists. */
function roundItems(config) {
  if (config.shortEWords || config.otherEWords || config.otherStartingWords) {
    const targets = [...(config.shortEWords || []), ...(config.otherEWords || [])].map((w) => ({ ...w, isTarget: true }));
    const others = (config.otherStartingWords || []).map((w) => ({ ...w, isTarget: false }));
    const t = seededShuffle(targets, 19).slice(0, config.roundTargetCount ?? 5);
    const d = seededShuffle(others, 36).slice(0, config.roundDistractorCount ?? 4);
    return seededShuffle([...t, ...d], 50);
  }
  return pickRoundItems(config, 19);
}

/**
 * Sound sort: drag (or tap-then-tap) each picture into the "starts with"
 * zone or the "not" zone. Sorted pictures pile up inside their zone.
 * Choice mode (drum parade): one picture at a time, two big buttons.
 * Completes when every item in the round is sorted correctly.
 */
export default function SoundSortGame({ letter, config, speak, sfx, onComplete, onWrong, reduceMotion }) {
  const finish = useCompleteOnce(onComplete);
  const { wrongId, flash } = useWrongFlash();
  const bp = useBreakpoint();
  const compact = bp === "xs";
  const { accent: letterAccent } = useLetterAccent(letter);
  const zones = config.zones || {
    target: { title: `Starts with ${letter}` },
    other: { title: `Not ${letter}` },
  };
  const items = useMemo(() => roundItems(config), [config]);
  const [remaining, setRemaining] = useState(items);
  const [placed, setPlaced] = useState({ target: [], other: [] });
  const [zoneWrong, setZoneWrong] = useState(null);
  const [zoneSparkle, setZoneSparkle] = useState({ target: 0, other: 0 });
  const [burst, setBurst] = useState(0);
  const total = items.length;
  const doneCount = placed.target.length + placed.other.length;
  const wrongTimer = useRef(null);
  const rootRef = useRef(null);
  const { w, h, tile } = useArenaMetrics(rootRef);

  const accept = useCallback(
    (item, intoTarget) => {
      const shouldTarget = item.isTarget !== false;
      if (intoTarget !== shouldTarget) {
        flash(itemKey(item));
        setZoneWrong(intoTarget ? "target" : "other");
        clearTimeout(wrongTimer.current);
        wrongTimer.current = setTimeout(() => setZoneWrong(null), 420);
        onWrong?.();
        speak?.(intoTarget ? "Not that side." : "Try the other side.", { dedupe: true });
        return false;
      }
      sfx?.("snap");
      const zoneId = intoTarget ? "target" : "other";
      setRemaining((r) => r.filter((x) => itemKey(x) !== itemKey(item)));
      setPlaced((p) => ({ ...p, [zoneId]: [...p[zoneId], item] }));
      setZoneSparkle((z) => ({ ...z, [zoneId]: z[zoneId] + 1 }));
      speak?.(item.word);
      return true;
    },
    [flash, onWrong, sfx, speak]
  );

  const onDrop = useCallback(
    ({ payload, targetId }) => {
      if (!targetId || !payload) return false;
      return accept(payload, targetId === "zone-target");
    },
    [accept]
  );

  const drag = useDragAndTap({ reduceMotion, sfx, onDrop });
  const targetRef = useDropTarget(drag.registerTarget, "zone-target", { zone: "target" });
  const otherRef = useDropTarget(drag.registerTarget, "zone-other", { zone: "other" });

  useEffect(() => () => clearTimeout(wrongTimer.current), []);

  useEffect(() => {
    if (doneCount >= total && total > 0) {
      sfx?.("correct");
      setBurst((b) => b + 1);
      const t = setTimeout(finish, reduceMotion ? 0 : 650);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [doneCount, total, finish, reduceMotion, sfx]);

  if (config.choices?.length) {
    return (
      <ChoiceMode
        letter={letter}
        config={config}
        items={items}
        speak={speak}
        sfx={sfx}
        onWrong={onWrong}
        finish={finish}
        reduceMotion={reduceMotion}
      />
    );
  }

  // Pieces in the tray are 1.1 tiles (shrinking to 0.85 when three rows must
  // fit a laptop arena); sorted pieces settle into the baskets at ~0.95 tiles
  // so the pile stays readable rather than shrinking to chips. The baskets
  // take at most 44% of the arena; the tray wraps inside what is left.
  const PILE = 0.95;
  const headerPx = compact ? 96 : 118;
  const zonesH = tile && h
    ? Math.round(Math.max(Math.min(h * 0.44, tile * PILE * 2 + headerPx), tile * PILE + headerPx - 22))
    : null;
  const trayPad = tile ? tile * 0.12 : 0;
  const fit = fitTiles({
    count: total,
    w: w ? w - trayPad * 2 : 0,
    h: zonesH != null ? h - zonesH - (compact ? 10 : 16) - trayPad * 2 : 0,
    tile,
    max: 1.1,
    min: 0.85,
    gap: 0.14,
  });
  const PIECE = fit.scale;
  const renderGhost = (p) => <Tile letter={letter} assetName={p.assetName} alt={p.word} scale={PIECE} />;
  const armed = Boolean(drag.picked);

  const zone = (id, ref, title, accent, deep, list) => {
    const hover = drag.hoverId === id;
    const zoneKey = id === "zone-target" ? "target" : "other";
    const wrong = zoneWrong === zoneKey;
    const chip = headerChip({ accent, deep, compact });
    const empty = list.length === 0;
    return (
      <div
        ref={ref}
        role="button"
        tabIndex={armed ? 0 : -1}
        aria-label={title}
        onClick={() => drag.tapTarget(id, { zone: id })}
        onKeyDown={(e) => {
          if (armed && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); drag.tapTarget(id, { zone: id }); }
        }}
        style={{
          ...frosted({
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            gap: compact ? 6 : 8,
            padding: compact ? 8 : 10,
            boxSizing: "border-box",
            position: "relative",
            cursor: armed ? "pointer" : "default",
            borderColor: wrong ? LEARN.color.coral : hover ? accent : armed ? `${accent}77` : LEARN.color.line,
            boxShadow: hover
              ? `0 0 0 6px ${accent}2e, ${LEARN.shadow.float}`
              : wrong
                ? `0 0 0 4px ${LEARN.color.coral}44, ${LEARN.shadow.card}`
                : `${LEARN.shadow.card}, inset 0 1px 0 rgba(255,255,255,1)`,
            transform: hover && !reduceMotion ? "scale(1.02)" : "scale(1)",
            transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
          }),
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", minWidth: 0 }}>
          <span style={chip.chip}>
            <span aria-hidden="true" style={chip.badge}>{id === "zone-target" ? letter : <CrossIcon size={compact ? 15 : 17} />}</span>
            <span style={chip.title}>{title}</span>
          </span>
        </div>
        <div
          style={{
            ...wellStyle({ accent, hover, armed, empty, wrong }),
            flex: 1,
            minHeight: `calc(var(--tile) * ${PILE} + 16px)`,
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignContent: empty ? "center" : "flex-start",
            alignItems: "center",
            rowGap: "calc(var(--tile) * 0.06)",
            overflowY: "auto",
            overflowX: "hidden",
            padding: compact ? "6px 4px" : "8px 6px",
            position: "relative",
          }}
        >
          <SparkleBurst trigger={zoneSparkle[zoneKey]} color={accent} reduceMotion={reduceMotion} distance={compact ? 50 : 70} count={10} />
          {empty ? (
            <DropHint accent={accent} lit={hover || armed} compact={compact} />
          ) : (
            <AnimatePresence initial={false}>
              {list.map((it, i) => (
                <motion.div
                  key={itemKey(it)}
                  initial={reduceMotion ? false : { scale: 0.3, opacity: 0, y: -16 }}
                  animate={{ scale: 1, opacity: 1, y: 0, rotate: ((i % 3) - 1) * 6 }}
                  transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 380, damping: 20 }}
                  style={{ marginLeft: i ? "calc(var(--tile) * -0.16)" : 0, marginTop: i % 2 ? "calc(var(--tile) * 0.05)" : 0, flexShrink: 0, position: "relative", zIndex: i }}
                >
                  <Tile letter={letter} assetName={it.assetName} alt={it.word} scale={PILE} solved={i === list.length - 1} lift={false} />
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </div>
      </div>
    );
  };

  return (
    <div ref={rootRef} style={{ ...gameShell.root, gap: 0 }}>
      <ConfettiBurst key={burst} active={burst > 0} originY={35} reduceMotion={reduceMotion} />
      <RoundCounter done={doneCount} total={total} accent={letterAccent} />
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: compact ? 10 : 16, paddingTop: compact ? 30 : 0 }}>
        {/* Baskets: only as tall as the pile needs, never more than 44% of the arena. */}
        <div
          style={{
            display: "flex", gap: compact ? 8 : 16,
            flex: "0 0 auto",
            height: zonesH != null ? zonesH : `min(calc(var(--arena-h, 500px) * 0.44), calc(var(--tile) * ${PILE * 2} + ${headerPx}px))`,
            minHeight: zonesH != null ? undefined : `calc(var(--tile) * ${PILE} + ${headerPx - 22}px)`,
          }}
        >
          {zone("zone-target", targetRef, zones.target?.title || `Starts with ${letter}`, LEARN.color.mint, LEARN.color.mintDeep, placed.target)}
          {zone("zone-other", otherRef, zones.other?.title || `Not ${letter}`, LEARN.color.coral, LEARN.color.coralDeep, placed.other)}
        </div>
        {/* Tray: a fixed column count from the fit so rows stay aligned as pieces leave. */}
        <div
          style={{
            ...gameShell.tray,
            gridTemplateColumns: `repeat(${fit.cols}, max-content)`,
            overflowY: fit.fits ? "visible" : "auto",
            flexShrink: 0,
            minHeight: 0,
          }}
          aria-label="Pictures to sort"
        >
          <AnimatePresence initial={false}>
            {remaining.map((it, i) => {
              const key = itemKey(it);
              return (
                <PopIn key={key} index={i} reduceMotion={reduceMotion} layout exit={reduceMotion ? undefined : { scale: 0.4, opacity: 0, transition: { duration: 0.16 } }}>
                  <ShakeWrap shake={wrongId === key} reduceMotion={reduceMotion} style={{ borderRadius: `calc(var(--tile) * ${PIECE} * 0.24)` }}>
                    <button
                      {...pieceButtonProps({
                        dragProps: drag.dragProps,
                        payload: it,
                        render: renderGhost,
                        label: it.word,
                        onTap: () => {
                          sfx?.("pop", { volume: 0.6 });
                          speak?.(it.word, { interrupt: true });
                          drag.pick(key, it);
                        },
                      })}
                      aria-pressed={drag.isPicked(key)}
                    >
                      <Tile letter={letter} assetName={it.assetName} alt={it.word} selected={drag.isPicked(key)} scale={PIECE} />
                    </button>
                  </ShakeWrap>
                </PopIn>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
      <drag.GhostLayer />
    </div>
  );
}

/** Drum parade: one picture at a time; tap the drum if it begins with the letter. */
function ChoiceMode({ letter, config, items, speak, sfx, onWrong, finish, reduceMotion }) {
  const { wrongId, flash } = useWrongFlash();
  const { accent: letterAccent, deep: letterDeep } = useLetterAccent(letter);
  const [idx, setIdx] = useState(0);
  const [pulse, setPulse] = useState(false);
  const total = items.length;
  const current = items[idx];
  const bp = useBreakpoint();
  const compact = bp === "xs";

  useEffect(() => {
    if (!current) return undefined;
    // First word waits for the stage prompt; later ones follow the tap.
    const t = setTimeout(() => speak?.(current.word), idx === 0 ? 900 : 120);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  if (!current) return <div style={gameShell.root} />;

  const choose = (c) => {
    const ok = (current.isTarget !== false) === !!c.meansTarget;
    if (!ok) {
      flash(itemKey(current));
      onWrong?.();
      return;
    }
    sfx?.(c.meansTarget ? "drumroll_short" : "pop", { volume: 0.8 });
    setPulse(true);
    setTimeout(() => setPulse(false), 300);
    const next = idx + 1;
    if (next >= total) {
      sfx?.("correct");
      setTimeout(finish, reduceMotion ? 0 : 500);
      setIdx(next);
      return;
    }
    setTimeout(() => setIdx(next), reduceMotion ? 0 : 320);
  };

  return (
    <div style={{ ...gameShell.root, alignItems: "center", justifyContent: "center", gap: compact ? 14 : 22 }}>
      <RoundCounter done={idx} total={total} accent={letterAccent} />
      <AnimatePresence mode="wait">
        <motion.div
          key={itemKey(current)}
          initial={reduceMotion ? false : { scale: 0.6, opacity: 0, y: 20 }}
          animate={{ scale: pulse && !reduceMotion ? 1.08 : 1, opacity: 1, y: 0 }}
          exit={reduceMotion ? undefined : { scale: 0.7, opacity: 0, x: -60, transition: { duration: 0.18 } }}
          transition={{ type: "spring", stiffness: 360, damping: 22 }}
          style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}
        >
          <ShakeWrap shake={wrongId === itemKey(current)} reduceMotion={reduceMotion} style={{ borderRadius: "calc(var(--tile) * 0.5)" }}>
            <button
              type="button"
              aria-label={`Hear ${current.word}`}
              onClick={() => speak?.(current.word, { interrupt: true })}
              className="learn-piece"
              style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer", borderRadius: "calc(var(--tile) * 0.5)" }}
            >
              <Tile letter={letter} assetName={current.assetName} alt={current.word} scale={compact ? 1.9 : 2.3} lift={false} />
            </button>
          </ShakeWrap>
          <span style={{ ...captionPill({ accent: letterAccent, deep: letterDeep }), fontSize: compact ? 20 : 24, marginTop: -4 }}>{current.word}</span>
        </motion.div>
      </AnimatePresence>
      <div style={{ display: "flex", gap: compact ? 10 : 16, flexWrap: "wrap", justifyContent: "center" }}>
        {config.choices.map((c, i) => {
          const accent = c.meansTarget ? LEARN.color.mint : LEARN.color.coral;
          const deep = c.meansTarget ? LEARN.color.mintDeep : LEARN.color.coralDeep;
          return (
            <PopIn key={c.id || c.label} index={i + 2} reduceMotion={reduceMotion}>
              <motion.button
                type="button"
                whileTap={reduceMotion ? undefined : { scale: 0.95, y: 3 }}
                onClick={() => choose(c)}
                className="learn-piece"
                style={{
                  ...gameShell.pill,
                  background: accent,
                  boxShadow: LEARN.shadow.sticker(deep),
                  minWidth: "calc(var(--tile) * 2)",
                  minHeight: 64,
                  padding: "12px 26px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  fontSize: 20,
                }}
              >
                <span>{c.label}</span>
                {c.subtitle && <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.92 }}>{c.subtitle}</span>}
              </motion.button>
            </PopIn>
          );
        })}
      </div>
    </div>
  );
}
